import { useMemo, useState } from "react";

import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listOrganizations } from "@/api/organizations";
import { getTenantWorkload, type WorkloadSummary } from "@/api/workload";
import { PageHeader } from "@/components/PageHeader";

function isoMonday(d: Date): string {
  const day = d.getDay();
  const offset = (day + 6) % 7;
  const m = new Date(d);
  m.setDate(d.getDate() - offset);
  return m.toISOString().slice(0, 10);
}

function isoSunday(d: Date): string {
  const day = d.getDay();
  const offset = (7 - day) % 7;
  const s = new Date(d);
  s.setDate(d.getDate() + offset);
  return s.toISOString().slice(0, 10);
}

export function WorkloadPage() {
  const { t } = useTranslation();
  const today = new Date();
  const [from, setFrom] = useState(isoMonday(today));
  const [to, setTo] = useState(isoSunday(today));
  const [orgId, setOrgId] = useState<string>("");

  const orgs = useQuery({
    queryKey: ["organizations-active-for-workload"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
  });

  const wl = useQuery({
    queryKey: ["tenant-workload", from, to, orgId],
    queryFn: () => getTenantWorkload(from, to, orgId || undefined),
    enabled: Boolean(from && to),
  });

  const stats = useMemo(() => {
    const items = wl.data?.items ?? [];
    return {
      practitioners: items.length,
      over48: items.filter((i) => i.avg_over_48h).length,
      peak60: items.filter((i) => i.peak_over_60h).length,
      totalHours: items.reduce((s, i) => s + i.total_hours, 0),
    };
  }, [wl.data]);

  return (
    <Box>
      <PageHeader
        title={t("workload.title")}
        subtitle={t("workload.subtitle")}
        helpTerm="surcharge"
      />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          type="date"
          label={t("workload.from")}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label={t("workload.to")}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          select
          label={t("workload.org")}
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">{t("workload.allOrgs")}</MenuItem>
          {(orgs.data?.items ?? []).map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <SummaryCard
            label={t("workload.kpi.practitioners")}
            value={stats.practitioners.toString()}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <SummaryCard
            label={t("workload.kpi.totalHours")}
            value={Math.round(stats.totalHours).toString()}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <SummaryCard
            label={t("workload.kpi.over48")}
            value={stats.over48.toString()}
            warn={stats.over48 > 0}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <SummaryCard
            label={t("workload.kpi.peak60")}
            value={stats.peak60.toString()}
            warn={stats.peak60 > 0}
            critical
          />
        </Grid>
      </Grid>

      {wl.isLoading && <CircularProgress size={24} />}
      {wl.data && wl.data.items.length === 0 && !wl.isLoading && (
        <Alert severity="info">{t("workload.empty")}</Alert>
      )}

      {wl.data && wl.data.items.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("workload.col.practitioner")}</TableCell>
                    <TableCell align="right">{t("workload.col.orgs")}</TableCell>
                    <TableCell align="right">{t("workload.col.avgWeek")}</TableCell>
                    <TableCell align="right">{t("workload.col.total")}</TableCell>
                    <TableCell>{t("workload.col.breakdown")}</TableCell>
                    <TableCell>{t("workload.col.status")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wl.data.items.map((i) => (
                    <WorkloadRow key={i.practitioner_id} item={i} />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}


function SummaryCard({
  label,
  value,
  warn,
  critical,
}: {
  label: string;
  value: string;
  warn?: boolean;
  critical?: boolean;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: critical && warn
          ? "error.main"
          : warn
            ? "warning.main"
            : "divider",
      }}
    >
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}


function WorkloadRow({ item }: { item: WorkloadSummary }) {
  const { t } = useTranslation();
  const avgPct = Math.min(100, (item.avg_weekly_hours / 60) * 100);
  return (
    <TableRow>
      <TableCell>{item.display_name}</TableCell>
      <TableCell align="right">{item.org_count}</TableCell>
      <TableCell align="right">
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
          <Typography
            variant="body2"
            color={item.peak_over_60h ? "error" : item.avg_over_48h ? "warning.main" : "text.primary"}
            sx={{ fontWeight: item.avg_over_48h ? 700 : 400 }}
          >
            {item.avg_weekly_hours.toFixed(1)}h
          </Typography>
          <Box sx={{ width: 50 }}>
            <LinearProgress
              variant="determinate"
              value={avgPct}
              color={item.peak_over_60h ? "error" : item.avg_over_48h ? "warning" : "primary"}
            />
          </Box>
        </Stack>
      </TableCell>
      <TableCell align="right">{item.total_hours.toFixed(1)}h</TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {Object.entries(item.by_kind)
            .filter(([, v]) => v > 0)
            .map(([k, v]) => (
              <Chip
                key={k}
                size="small"
                variant="outlined"
                label={`${t(`slotKind.${k}`)}: ${v.toFixed(1)}h`}
                color={k === "GARDE" ? "error" : k === "DEMI_GARDE" ? "warning" : "default"}
              />
            ))}
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5}>
          {item.peak_over_60h && (
            <Tooltip title={t("workload.tooltip.peak60")}>
              <Chip
                size="small"
                color="error"
                icon={<WarningIcon />}
                label={t("workload.status.peak60")}
              />
            </Tooltip>
          )}
          {item.avg_over_48h && !item.peak_over_60h && (
            <Tooltip title={t("workload.tooltip.over48")}>
              <Chip
                size="small"
                color="warning"
                icon={<WarningIcon />}
                label={t("workload.status.over48")}
              />
            </Tooltip>
          )}
          {!item.avg_over_48h && !item.peak_over_60h && (
            <Chip size="small" color="success" variant="outlined" label={t("workload.status.ok")} />
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
